import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_LINKS = [
  { label: "Services", id: "services" },
  { label: "Commerce", id: "commerce" },
  { label: "Systems", id: "systems" },
  { label: "Work", id: "work" },
  { label: "About", id: "about" },
  { label: "FAQ", id: "faq" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 80);
  };

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-border"
          : "bg-background/40 backdrop-blur-md border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <button
          onClick={() => scrollToId("home")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo-button"
        >
          <img
            src="/fcy-mark.png"
            alt="FCY IT Consultancy logo"
            className="w-9 h-9 object-contain drop-shadow-[0_0_14px_rgba(214,179,106,0.25)]"
          />
          <span className="hidden sm:block text-left leading-tight">
            <span className="block text-sm font-semibold tracking-wide text-foreground">
              FCY IT CONSULTANCY
            </span>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              London · Est. 2021
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToId(link.id)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 relative after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-[width] after:duration-300 hover:after:w-full"
              data-testid={`nav-${link.id}-link`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => scrollToId("contact")}
            className="cta-shimmer hidden sm:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl px-5 shadow-[0_8px_30px_rgba(214,179,106,0.25)] hover:-translate-y-px transition-transform duration-200"
            data-testid="nav-contact-link"
          >
            Contact Us
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="lg:hidden border-border bg-secondary/50"
                data-testid="nav-mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-card border-border w-72">
              <div className="mt-10 flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => handleNav(link.id)}
                    className="text-left px-3 py-3 rounded-lg text-base text-foreground hover:bg-secondary transition-colors duration-200"
                    data-testid={`nav-mobile-${link.id}-link`}
                  >
                    {link.label}
                  </button>
                ))}
                <Button
                  onClick={() => handleNav("contact")}
                  className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl"
                  data-testid="nav-mobile-contact-button"
                >
                  Contact Us
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
};
